/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useHistory} from 'react-router-dom'
import {useFormik} from 'formik'
import {login, loginPageSettings} from '../redux/AuthCRUD'
import {useIntl} from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import {useEffectOnce} from 'react-use'
import {TermsModal} from './TermsModal'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .required('Username is required')
    .min(3, 'Username must be between 3 and 128 characters.')
    .max(128, 'Username must be between 3 and 128 characters.')
    .email('Wrong email format'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be between 8 and 128 characters.')
    .max(128, 'Password must be between 8 and 128 characters.'),
})

const initialValues = {
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

interface IProps {
  handleLogin: (
    token: string,
    email: string,
    twoFactorEnabled: boolean | null,
    twoFactorConnected: boolean,
    twoFactorMethod: number | 0
  ) => void
}

const recaptchaRef = React.createRef<ReCAPTCHA>()

//export function Login() {
const Login: React.FC<IProps> = ({handleLogin}) => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const intl = useIntl()
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaEnabled, setCaptchaEnabled] = useState(false)
  const [registerNowEnabled, setRegisterNowEnabled] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showTerms, setShowTerms] = useState(false)

  const buildForgotPasswordLink = (email: string) => {
    if (!email) {
      return '/auth/forgot-password'
    }

    return `/auth/forgot-password?email=${encodeURIComponent(email)}`
  }

  const formik = useFormik({
    initialValues,
    validateOnMount: true,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting, setFieldValue}) => {
      setLoading(true)
      setStatus(undefined)
      try {
        const {data} = await login(values.email, values.password, captchaToken)
        const {token, twoFactorEnabled, twoFactorConnected, twoFactorMethod} = data
        handleLogin(token, values.email, twoFactorEnabled, twoFactorConnected, twoFactorMethod)
      } catch (error: any) {
        const response = error?.response
        const code = response?.data?.code ?? response?.data?.Code
        const message = response?.data?.message ?? response?.data?.Message

        recaptchaRef.current?.reset()
        setCaptchaToken(null)

        if (code === 'PASSWORD_EXPIRED') {
          const challengeToken =
            response?.data?.challengeToken ?? response?.data?.ChallengeToken ?? ''
          history.push('/auth/password-expired', {
            email: values.email,
            challengeToken,
          })
          return
        }

        if (!response || [502, 503, 504].includes(response?.status)) {
          setStatus('Login service unavailable. Please try again.')
          return
        }

        if (response?.status === 429) {
          setStatus('Too many attempts. Try again later or reset your password.')
          return
        }

        setStatus(message ?? 'Something went wrong. Please try again.')
      } finally {
        setFieldValue('password', '', false)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const onReCAPTCHAChange = async (captchaToken: string | null) => {
    if (captchaToken) {
      setCaptchaToken(captchaToken)
    }
  }

  useEffectOnce(() => {
    loginPageSettings().then(({data: {captchaEnable, registerNowEnabled}}) => {
      setCaptchaEnabled(captchaEnable)
      setRegisterNowEnabled(!!registerNowEnabled)
    })
  })

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-10'>
          <h1 className='text-dark mb-3'>Log In</h1>
          <p className='text-muted'>Loren ipsum dolor sit amet adipiscing elit.</p>
        </div>
        {/* begin::Heading */}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold text-danger'>{formik.status}</div>
          </div>
        )}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <label className='form-label fs-6 fw-bolder text-dark d-none'>Username</label>
          <input
            placeholder='Username'
            {...formik.getFieldProps('email')}
            className={clsx('form-control form-control-lg form-control-solid', {
              'is-invalid': formik.touched.email && formik.errors.email,
            })}
            type='email'
            name='email'
            autoComplete='username'
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <span role='alert' className='text-danger'>
                {formik.errors.email}
              </span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-between mt-n5'>
            <div className='d-flex flex-stack mb-2'>
              {/* begin::Label */}
              <label className='form-label fw-bolder text-dark fs-6 mb-0 d-none'>Password</label>
              {/* end::Label */}
            </div>
          </div>
          <div className='position-relative'>
            <input
              placeholder='Password'
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              style={{paddingRight: 'calc(1.5em + 2.6rem)'}}
              {...formik.getFieldProps('password')}
              className={clsx('form-control form-control-lg form-control-solid', {
                'is-invalid': formik.touched.password && formik.errors.password,
              })}
            />
            <button
              type='button'
              className='btn btn-sm position-absolute top-50 end-0 me-10 translate-middle-y me-2 p-0 border-0 bg-transparent text-muted'
              style={{width: '2rem', height: '2rem'}}
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert' className='text-danger'>
                  {formik.errors.password}
                </span>
              </div>
            </div>
          )}
        </div>

        {captchaEnabled && (
          <div className='d-flex justify-content-center align-items-center mb-5'>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey='6LcODrgpAAAAADg3cCPtrUHNltj3NjDTMl74sJXb'
              onChange={onReCAPTCHAChange}
            />
          </div>
        )}

        {/* end::Form group */}

        {/* begin::Action */}
        <div className='text-center'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={
              formik.isSubmitting ||
              loading ||
              !formik.isValid ||
              !formik.values.email ||
              !formik.values.password
            }
          >
            {!loading && <span className='indicator-label'>Log In</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Action */}
      </form>
      <hr className='m-5'></hr>
      <div className='d-flex flex-column justify-content-center align-items-center mb-5'>
        <Link
          to={buildForgotPasswordLink(formik.values.email)}
          className='link-primary fw-bolder mb-5 fs-6'
          style={{marginLeft: '5px'}}
        >
          Forgot your password?
        </Link>
        {registerNowEnabled ? (
          <div className='text-gray-400 mb-5 fs-6'>
            Don't have an account?{' '}
            <Link to='/auth/registration' className='link-primary fw-bolder'>
              Register Now
            </Link>
          </div>
        ) : null}
      </div>
      <TermsModal show={showTerms} onClose={() => setShowTerms(false)} />
    </>
  )
}

export {Login}
